import { useState, useEffect } from 'react';
import axiosDotnet from '../services/axiosDotnet';
import { showError, showSuccess, showWarning } from '../services/reactToastify';

export default function AddProjectMemberModal({ projectId, workspaceId, isOpen, onClose, onMemberAdded }) {
    const [workspaceMembers, setWorkspaceMembers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [role, setRole] = useState('MEMBER');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchWorkspaceMembers();
        }
    }, [isOpen]);

    const fetchWorkspaceMembers = async () => {
        try {
            const res = await axiosDotnet.get(`/api/workspaces/${workspaceId}/members`);
            // Filter only APPROVED members
            setWorkspaceMembers(res.data.filter(m => m.status === "APPROVED"));
        } catch (err) {
            console.error(err);
        }
    };

    const handleAddMember = async () => {
        if (!selectedUserId) return showWarning("Please select a member");

        setLoading(true);
        try {
            await axiosDotnet.post(`/api/projects/${projectId}/members`, {
                userId: selectedUserId,
                role: role
            });

            showSuccess("Member added to project successfully!");
            onMemberAdded?.();
            onClose();
        } catch (err) {
            showError(err?.response?.data?.message || "Failed to add member! You must be admin of project or workspace to add members");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="bg-zinc-900 border border-zinc-700 rounded-3xl w-full max-w-md p-8">
                <h2 className="text-2xl font-semibold mb-6">Add Member to Project</h2>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm text-zinc-400 mb-2">Select Member</label>
                        <select
                            value={selectedUserId}
                            onChange={(e) => setSelectedUserId(e.target.value)}
                            className="w-full cursor-pointer bg-zinc-800 border border-zinc-700 rounded-2xl px-4 py-3 text-white"
                        >
                            <option value="">Select from Workspace Members</option>
                            {workspaceMembers.map(member => (
                                <option key={member.userId} value={member.userId}>
                                    {member.name} ({member.email})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm text-zinc-400 mb-2">Role in Project</label>
                        <select
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                            className="w-full cursor-pointer bg-zinc-800 border border-zinc-700 rounded-2xl px-4 py-3 text-white"
                        >
                            <option value="MEMBER">Member</option>
                            <option value="ADMIN">Admin</option>
                            <option value="VIEWER">Viewer</option>
                        </select>
                    </div>
                </div>

                <div className="flex gap-3 mt-8">
                    <button
                        onClick={onClose}
                        className="flex-1 cursor-pointer py-3 border border-zinc-700 rounded-2xl hover:bg-zinc-800"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleAddMember}
                        disabled={loading || !selectedUserId}
                        className="flex-1 cursor-pointer py-3 bg-violet-600 hover:bg-violet-500 rounded-2xl font-semibold disabled:opacity-50"
                    >
                        {loading ? "Adding..." : "Add Member"}
                    </button>
                </div>
            </div>
        </div>
    );
}